import * as React from "react";
import PropTypes from "prop-types";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { DialogActions } from "@material-ui/core";
import SexyButton from "../../theme/SexyButton";
import StatusMessage from "../../theme/StatusMessage";
import { StyledForm } from "../../theme/Form";
import logger from "../../util/logger";
import Sections from "./Sections";
import CompleteSection from "./CompleteSection";
import * as utils from "./utils";

const PurchaseForm = React.memo(function PurchaseForm({
  purchaseDetails,
  onSubmit,
  showBillingAddress,
  showShippingAddress,
  purchaseComplete,
  setPurchaseComplete,
  purchasePending,
  setPurchasePending,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [statusMessage, setStatusMessage] = React.useState(null);

  const [state, dispatch] = React.useReducer(
    utils.genericReducer,
    utils.createInitialState(purchaseDetails),
  );

  const showError = (err) => {
    setPurchasePending(false);
    setStatusMessage({ message: err.message, type: "error" });

    setTimeout(() => {
      setStatusMessage(null);
    }, 3000);
  };

  const wrappedOnSubmit = async (evt) => {
    evt.preventDefault();

    if (!stripe || !elements || purchaseComplete || purchasePending) {
      return;
    }

    setPurchasePending(true);

    try {
      const action = utils.getActionForState({ state, stripe });

      const response = await action(
        utils.composeStripeInitForState({ purchaseDetails, elements, state }),
      );

      const { error } = response;

      if (error) {
        return showError(error);
      }

      setTimeout(() => {
        setPurchasePending(false);
        setPurchaseComplete(true);
      }, 200);
    } catch (err) {
      logger.error(err);
      showError(err);
    }
  };

  return (
    <StyledForm onSubmit={wrappedOnSubmit}>
      {!purchaseComplete && (
        <Sections
          state={state}
          dispatch={dispatch}
          showBillingAddress={showBillingAddress}
          showShippingAddress={showShippingAddress}
        />
      )}

      {purchaseComplete && (
        <CompleteSection purchaseDetails={purchaseDetails} />
      )}

      <DialogActions>
        {statusMessage && <StatusMessage {...statusMessage} />}

        <SexyButton
          isLoading={purchasePending}
          type={purchaseComplete ? "button" : "submit"}
          onClick={purchaseComplete ? onSubmit : void 0}
          disabled={purchasePending || !stripe || !elements}
        >
          {purchaseComplete ? "Done" : "Pay Now"}
        </SexyButton>
      </DialogActions>
    </StyledForm>
  );
});

PurchaseForm.propTypes = {
  purchaseDetails: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showBillingAddress: PropTypes.bool,
  showShippingAddress: PropTypes.bool,
  purchaseComplete: PropTypes.bool,
  setPurchaseComplete: PropTypes.func.isRequired,
  purchasePending: PropTypes.bool,
  setPurchasePending: PropTypes.func.isRequired,
};

export default PurchaseForm;
